/**
 * Helpers compartilhados das telas de Orcamento + Pedido.
 *
 * Mapeamento situacao -> tone de cor (paleta funcional do design system):
 *   info    = em progresso normal
 *   warning = atencao requerida
 *   success = conclusao positiva
 *   danger  = problema / cancelado / recusado
 *   neutral = sem destaque
 */

import { formatarDataLocal } from "../../utils/dinheiro";

export const SITUACAO_TONE_ORCAMENTO = {
  RASCUNHO: "neutral",
  EM_NEGOCIACAO: "info",
  APROVADO: "success",
  CONVERTIDO: "success",
  RECUSADO: "danger",
  EXPIRADO: "neutral",
};

export const SITUACAO_TONE_PEDIDO = {
  RECEBIDO: "info",
  EM_PRODUCAO: "info",
  EM_EXPEDICAO: "warning",
  ENTREGUE: "success",
  CANCELADO: "danger",
};

export const PRIORIDADE_TONE = {
  URGENTE: "danger",
  EXPRESS: "warning",
  NORMAL: "neutral",
  CORTESIA: "info",
};

// Bloco B: status temporal computed pelo backend
export const STATUS_TEMPORAL_TONE = {
  EM_ABERTO: "success",
  QUASE_PERDIDO: "warning",
  PERDIDO: "danger",
};

export const STATUS_TEMPORAL_LABEL = {
  EM_ABERTO: "Em aberto",
  QUASE_PERDIDO: "Quase perdido",
  PERDIDO: "Perdido",
};

export function fmtMoeda(valor) {
  if (valor === null || valor === undefined || valor === "") return "—";
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(Number(valor));
  } catch {
    return String(valor);
  }
}

/**
 * Data para leitura, sem o off-by-one de fuso.
 *
 * Reteste 2026-08-18: o vendedor escolhia 21/08 no follow-up e a tela
 * mostrava 20/08. A data GRAVA certa — o erro é de leitura:
 * `new Date("2026-08-21")` é meia-noite **UTC**, que em São Paulo é 21h
 * do dia 20. Campos date-only (`data_proxima_acao`, validade, prazos)
 * caem todos nessa armadilha.
 *
 * `formatarDataLocal` (utils/dinheiro) é o helper canônico do projeto e
 * já corta o caso date-only antes de tocar em `Date`.
 */
export function fmtData(iso, withTime = false) {
  if (!iso) return "—";
  if (!withTime) return formatarDataLocal(iso);
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

export function fmtNumero(valor, casas = 0) {
  if (valor === null || valor === undefined || valor === "") return "—";
  try {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    }).format(Number(valor));
  } catch {
    return String(valor);
  }
}

/**
 * Bloco H — Helper para sugerir data de follow-up.
 *
 * Calcula `hoje + N dias uteis` (pula sabado/domingo, sem feriados — mesma
 * estrategia leve do backend `ConfiguracaoComercial.contagem=UTEIS`).
 * Retorna no formato "YYYY-MM-DD" (compativel com Input type=date).
 */
export function proximaDataUtilSugerida(diasUteis = 3) {
  const data = new Date();
  let adicionados = 0;
  while (adicionados < diasUteis) {
    data.setDate(data.getDate() + 1);
    const dow = data.getDay(); // 0=dom, 6=sab
    if (dow !== 0 && dow !== 6) adicionados += 1;
  }
  // formata YYYY-MM-DD sem timezone shift
  const yyyy = data.getFullYear();
  const mm = String(data.getMonth() + 1).padStart(2, "0");
  const dd = String(data.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
